import { useState, useEffect } from 'react';
import { siteConfig } from '@/data/site';

const GitHubCommitCount = () => {
  const [commitCount, setCommitCount] = useState<number>(0);
  const [displayCount, setDisplayCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [apiResponse, setApiResponse] = useState<any>(null);

  // Animate counter from 0 to target
  useEffect(() => {
    if (commitCount === 0) return;

    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = commitCount / steps;
    const stepDuration = duration / steps;

    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= commitCount) {
        setDisplayCount(commitCount);
        clearInterval(timer);
      } else {
        setDisplayCount(Math.floor(current));
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [commitCount]);

  useEffect(() => {
    const fetchCommitCount = async () => {
      try {
        // Extract username from GitHub URL
        const githubUrl = siteConfig.social.github;
        const username = githubUrl.split('github.com/')[1]?.split('/')[0];
        
        if (!username) {
          setLoading(false);
          return;
        }

        // Try multiple API endpoints for better reliability
        let count = 0;
        
        // Method 1: GitHub contributions API - Get ALL years' data
        try {
          // Fetch without year parameter to get all years
          const response = await fetch(
            `https://github-contributions-api.jogruber.de/v4/${username}`,
            {
              headers: {
                Accept: 'application/json',
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            setApiResponse(data); // Store for debugging
            
            // Check if API provides totals for all years
            if (data.total && typeof data.total === 'object') {
              // Sum all years from the total object
              count = Object.values(data.total).reduce<number>((sum: number, yearCount: unknown) => {
                return sum + (typeof yearCount === 'number' ? yearCount : 0);
              }, 0);
            } else if (data.contributions) {
              // If no total object, calculate from all contributions
              data.contributions.forEach((week: any) => {
                week.days.forEach((day: any) => {
                  count += day.count;
                });
              });
            }
            
            if (count > 0) {
              setCommitCount(count);
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          console.log('Contributions API failed, trying alternative...');
        }

        // Method 2: Alternative API
        try {
          const altResponse = await fetch(
            `https://api.github.com/users/${username}/events/public?per_page=100`,
            {
              headers: {
                Accept: 'application/vnd.github.v3+json',
              },
            }
          );
          
          if (altResponse.ok) {
            const events = await altResponse.json();
            const pushEvents = events.filter((event: any) => event.type === 'PushEvent');
            let totalCommits = 0;
            pushEvents.forEach((event: any) => {
              totalCommits += event.payload.commits?.length || 0;
            });
            // Estimate based on recent activity
            if (totalCommits > 0) {
              setCommitCount(totalCommits * 15); // Rough estimate for year
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          console.log('Events API also failed');
        }

        // If all APIs fail, set a placeholder
        setCommitCount(0);
      } catch (error) {
        console.error('Error fetching commit count:', error);
        setCommitCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCommitCount();
    
    // Refresh every 10 minutes
    const interval = setInterval(fetchCommitCount, 10 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Calculate progress towards next milestone (10k, 20k, 50k, 100k, etc.)
  const getMilestone = (count: number) => {
    if (count < 10000) return { current: count, next: 10000, label: '10k' };
    if (count < 20000) return { current: count, next: 20000, label: '20k' };
    if (count < 50000) return { current: count, next: 50000, label: '50k' };
    if (count < 100000) return { current: count, next: 100000, label: '100k' };
    return { current: count, next: count + 50000, label: 'next' };
  };

  const milestone = getMilestone(commitCount);
  const progress = commitCount > 0 ? (milestone.current / milestone.next) * 100 : 0;

  return (
    <div className="flex flex-col items-center gap-1.5 w-full max-w-xs">
      {loading ? (
        <div className="text-xs text-slate-400 animate-pulse">Loading commits...</div>
      ) : (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg sm:text-xl font-mono font-bold text-cyan-400 tabular-nums">
              {displayCount.toLocaleString()}
            </span>
            <span className="text-xs text-slate-400 font-mono">commits</span>
          </div>
          
          {/* Progress bar */}
          <div className="w-full h-0.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          
          {/* Milestone indicator */}
          <div className="text-xs text-slate-500 font-mono">
            {milestone.current.toLocaleString()} / {milestone.next.toLocaleString()} ({milestone.label})
          </div>
        </>
      )}
    </div>
  );
};

export default GitHubCommitCount;
